<script setup>
import { ref, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useAlert, useTrack } from 'dashboard/composables';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import contactFilterItems from 'dashboard/routes/dashboard/contacts/contactFilterItems';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import countries from 'shared/constants/countries';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';

import ContactsHeader from 'dashboard/components-next/Contacts/ContactsHeader/ContactHeader.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import ContactExportDialog from 'dashboard/components-next/Contacts/ContactsForm/ContactExportDialog.vue';
import ContactImportDialog from 'dashboard/components-next/Contacts/ContactsForm/ContactImportDialog.vue';
import CreateSegmentDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateSegmentDialog.vue';
import DeleteSegmentDialog from 'dashboard/components-next/Contacts/ContactsForm/DeleteSegmentDialog.vue';
import ContactsFilter from 'dashboard/components-next/filter/ContactsFilter.vue';

const props = defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  segmentsId: { type: [String, Number], default: 0 },
  activeSegment: { type: Object, default: null },
  hasAppliedFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
  selectedContactIds: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'update:sort',
  'search',
  'applyFilter',
  'clearFilters',
]);

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const createNewContactDialogRef = ref(null);
const contactExportDialogRef = ref(null);
const contactImportDialogRef = ref(null);
const createSegmentDialogRef = ref(null);
const deleteSegmentDialogRef = ref(null);

const DEFAULT_GROUP = () => ({
  conditions: [
    {
      attributeKey: 'name',
      filterOperator: 'equal_to',
      values: '',
      queryOperator: 'and',
      attributeModel: 'standard',
    },
  ],
  groupOperator: 'AND',
});

const showFiltersModal = ref(false);
const appliedFilter = ref([DEFAULT_GROUP()]);
const segmentsQuery = ref({});

const appliedFilters = useMapGetter('contacts/getAppliedContactFiltersV4');
const contactAttributes = useMapGetter('attributes/getContactAttributes');
const labels = useMapGetter('labels/getLabels');
const hasActiveSegments = computed(
  () => props.activeSegment && props.segmentsId !== 0
);
const activeSegmentName = computed(() => props.activeSegment?.name);

const openCreateNewContactDialog = async () => {
  await createNewContactDialogRef.value?.contactsFormRef.resetValidation();
  createNewContactDialogRef.value?.dialogRef.open();
};
const openContactImportDialog = () =>
  contactImportDialogRef.value?.dialogRef.open();
const openContactExportDialog = () =>
  contactExportDialogRef.value?.dialogRef.open();
const openCreateSegmentDialog = () =>
  createSegmentDialogRef.value?.dialogRef.open();
const openDeleteSegmentDialog = () =>
  deleteSegmentDialogRef.value?.dialogRef.open();

const onCreate = async contact => {
  try {
    await store.dispatch('contacts/create', contact);
    createNewContactDialogRef.value?.onSuccess();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
    );
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS_DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER_DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const onImport = async file => {
  try {
    await store.dispatch('contacts/import', file);
    contactImportDialogRef.value?.dialogRef.close();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.SUCCESS_MESSAGE')
    );
    useTrack(CONTACTS_EVENTS.IMPORT_SUCCESS);
  } catch (error) {
    useAlert(
      error.message ??
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.ERROR_MESSAGE')
    );
    useTrack(CONTACTS_EVENTS.IMPORT_FAILURE);
  }
};

const onExport = async query => {
  try {
    await store.dispatch('contacts/export', query);
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error.message ||
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.EXPORT_CONTACT.ERROR_MESSAGE')
    );
  }
};

const onCreateSegment = async payload => {
  try {
    let query = segmentsQuery.value;

    // If contacts are selected, create a query based on selected IDs
    if (props.selectedContactIds && props.selectedContactIds.length > 0) {
      query = {
        payload: [
          {
            attribute_key: 'id',
            filter_operator: 'equal_to',
            values: [...props.selectedContactIds],
            query_operator: null, // For single condition, query_operator should be null
          },
        ],
      };
    }

    const payloadData = {
      ...payload,
      query,
    };

    const response = await store.dispatch('customViews/create', payloadData);
    createSegmentDialogRef.value?.dialogRef.close();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.SUCCESS_MESSAGE')
    );
    const segmentId = response?.data?.id;
    if (!segmentId) return;
    // Navigate to the created segment
    router.push({
      name: 'contacts_dashboard_segments_index',
      params: { segmentId },
      query: { page: 1 },
    });
  } catch {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.ERROR_MESSAGE')
    );
  }
};

const onDeleteSegment = async payload => {
  try {
    await store.dispatch('customViews/delete', {
      id: Number(props.segmentsId),
      ...payload,
    });
    router.push({
      name: 'contacts_dashboard_index',
      query: {
        page: 1,
      },
    });
    deleteSegmentDialogRef.value?.dialogRef.close();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.DELETE_SEGMENT.SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.DELETE_SEGMENT.ERROR_MESSAGE')
    );
  }
};

const closeAdvanceFiltersModal = () => {
  showFiltersModal.value = false;
  appliedFilter.value = [DEFAULT_GROUP()];
};

const clearFilters = async () => {
  await store.dispatch('contacts/clearContactFilters');
  emit('clearFilters');
};

const generateValues = item => {
  const key = item.attribute_key || item.attributeKey;
  if (key === 'content') {
    const values = item.values || '';
    return typeof values === 'string' ? values.split(',') : values;
  }
  if (Array.isArray(item.values)) {
    return item.values[0]?.id ? item.values.map(v => v.id) : item.values;
  }
  if (typeof item.values === 'object' && item.values !== null) {
    return [item.values.id];
  }
  if (!item.values) return [];
  return [item.values];
};

const buildGroupedApiPayload = groups => {
  return {
    groups: groups.map(group => ({
      group_operator: (group.groupOperator || 'AND').toUpperCase(),
      conditions: group.conditions.map((item, idx) => {
        const isLast = idx === group.conditions.length - 1;
        // The queryOperator on condition[idx+1] describes how condition[idx] joins condition[idx+1].
        // So we read the *next* condition's queryOperator for the current condition's query_operator.
        const nextItem = group.conditions[idx + 1];
        const queryOp = isLast
          ? null
          : (
              nextItem?.queryOperator ||
              nextItem?.query_operator ||
              'AND'
            ).toUpperCase();
        return {
          attribute_key: item.attributeKey || item.attribute_key,
          attribute_model: item.attributeModel || item.attribute_model,
          filter_operator: item.filterOperator || item.filter_operator,
          query_operator: queryOp,
          values: generateValues(item),
          custom_attribute_type:
            item.customAttributeType || item.custom_attribute_type || '',
        };
      }),
    })),
  };
};

const onApplyFilter = async payload => {
  // payload is now an array of groups from ContactsFilter
  const apiPayload = buildGroupedApiPayload(payload);
  store.dispatch('contacts/setContactFilters', apiPayload);
  segmentsQuery.value = apiPayload;
  emit('applyFilter', apiPayload);
  showFiltersModal.value = false;
};

const onUpdateSegment = async (payload, segmentName) => {
  const apiPayload = buildGroupedApiPayload(payload);
  const payloadData = {
    ...props.activeSegment,
    name: segmentName,
    query: apiPayload,
  };
  await store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
};

const setParamsForEditSegmentModal = () => {
  return {
    countries,
    filterTypes: contactFilterItems,
    allCustomAttributes: useSnakeCase(contactAttributes.value),
    labels: labels.value || [],
  };
};

const initializeSegmentToFilterModal = segment => {
  const segmentQuery = unref(segment)?.query;

  // New grouped format
  if (segmentQuery?.groups) {
    appliedFilter.value = segmentQuery.groups.map(group => ({
      groupOperator: group.groupOperator || 'AND',
      conditions: group.conditions.map(filter => {
        const transformed = useCamelCase(filter);
        const values = Array.isArray(transformed.values)
          ? generateValuesForEditCustomViews(
              useSnakeCase(transformed),
              setParamsForEditSegmentModal()
            )
          : [];
        return {
          attributeKey: transformed.attributeKey,
          attributeModel: transformed.attributeModel,
          customAttributeType: transformed.customAttributeType,
          filterOperator: transformed.filterOperator,
          queryOperator: transformed.queryOperator ?? 'and',
          values,
        };
      }),
    }));
    return;
  }

  // Legacy flat format
  const query = segmentQuery?.payload;
  if (!Array.isArray(query)) return;

  const conditions = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditSegmentModal()
        )
      : [];
    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [{ conditions, groupOperator: 'AND' }];
};

const onToggleFilters = () => {
  appliedFilter.value = [];
  if (hasActiveSegments.value) {
    initializeSegmentToFilterModal(props.activeSegment);
  } else {
    let existingFilters;
    if (props.hasAppliedFilters && appliedFilters.value?.groups) {
      // Store data is in snake_case API format — convert back to camelCase for the UI.
      // query_operator on condition[idx] means "how condition[idx] joins condition[idx+1]",
      // but the UI displays it as the queryOperator on condition[idx+1] (the toggle shown before it).
      existingFilters = appliedFilters.value.groups.map(group => {
        const raw = group.conditions || [];
        return {
          groupOperator: group.groupOperator || group.group_operator || 'AND',
          conditions: raw.map((c, idx) => {
            const prevOp =
              idx === 0
                ? 'and'
                : (
                    raw[idx - 1]?.queryOperator ||
                    raw[idx - 1]?.query_operator ||
                    'and'
                  ).toLowerCase();
            return {
              attributeKey: c.attributeKey || c.attribute_key,
              filterOperator: c.filterOperator || c.filter_operator,
              queryOperator: idx === 0 ? 'and' : prevOp,
              values: c.values ?? '',
              attributeModel:
                c.attributeModel || c.attribute_model || 'standard',
              customAttributeType:
                c.customAttributeType || c.custom_attribute_type || '',
            };
          }),
        };
      });
    } else {
      existingFilters = [DEFAULT_GROUP()];
    }
    appliedFilter.value = existingFilters;
  }
  showFiltersModal.value = true;
};

defineExpose({
  onToggleFilters,
});
</script>

<template>
  <ContactsHeader
    :show-search="showSearch"
    :search-value="searchValue"
    :active-sort="activeSort"
    :active-ordering="activeOrdering"
    :header-title="headerTitle"
    :is-segments-view="hasActiveSegments"
    :is-label-view="isLabelView"
    :is-active-view="isActiveView"
    :has-active-filters="hasAppliedFilters"
    :button-label="t('CONTACTS_LAYOUT.HEADER.MESSAGE_BUTTON')"
    :selected-contact-ids="selectedContactIds"
    @search="emit('search', $event)"
    @update:sort="emit('update:sort', $event)"
    @add="openCreateNewContactDialog"
    @import="openContactImportDialog"
    @export="openContactExportDialog"
    @filter="onToggleFilters"
    @create-segment="openCreateSegmentDialog"
    @delete-segment="openDeleteSegmentDialog"
  >
    <template #filter>
      <div
        class="absolute mt-1 ltr:-right-52 rtl:-left-52 sm:ltr:right-0 sm:rtl:left-0 top-full"
      >
        <ContactsFilter
          v-if="showFiltersModal"
          v-model="appliedFilter"
          :segment-name="activeSegmentName"
          :is-segment-view="hasActiveSegments"
          @apply-filter="onApplyFilter"
          @update-segment="onUpdateSegment"
          @close="closeAdvanceFiltersModal"
          @clear-filters="clearFilters"
        />
      </div>
    </template>
  </ContactsHeader>

  <CreateNewContactDialog ref="createNewContactDialogRef" @create="onCreate" />
  <ContactExportDialog ref="contactExportDialogRef" @export="onExport" />
  <ContactImportDialog ref="contactImportDialogRef" @import="onImport" />
  <CreateSegmentDialog ref="createSegmentDialogRef" @create="onCreateSegment" />
  <DeleteSegmentDialog ref="deleteSegmentDialogRef" @delete="onDeleteSegment" />
</template>
